import moment from "moment/moment";
import {Link, router, useRemember} from "@inertiajs/react";
import {bytesToSize, getLanguageName, secondsToDuration, ucFirst} from "@/utils/textFormatting";
import {cancelJob, deleteJob, isJobRunning} from "@/services/extractionJobManager";
import {
    ExtractionJob,
    Folder,
    PaginatedExtractionJob,
    PersistedExtractionJob,
    JobTableItem
} from "@/types";
import toast from "react-hot-toast";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowAltCircleDown as faCircleDown,
    faMessage,
    faPenToSquare as faPen,
    faTrashCan,
    faFolder
} from "@fortawesome/free-regular-svg-icons";
import {
    faCircleXmark,
    faDownload,
    faEllipsis,
    faRotateRight,
    faSearch,
    faXmark,
    faArrowRight
} from "@fortawesome/free-solid-svg-icons";
import JobStatusIndicator from "@/Pages/Dashboard/Partials/JobStatusIndicator";
import Dropdown from "@/Components/Dropdown";
import {Tooltip} from "flowbite-react";
import Checkbox from "@/Components/Checkbox";
import React, {useEffect, useMemo, useRef, useState} from "react";
import SecondaryButton from "@/Components/SecondaryButton";
import Popover from "@/Components/Popover";
import {PaginatedSource} from "@/hooks/usePaginatedSource";
import PaginationControl from "@/Components/PaginationControl";
import axios from "axios";
import TranslateIcon from "@/Components/Icons/TranslateIcon";
import {useConfirm} from "@/Components/ConfirmDialog";
import FolderMoveModal from "@/Pages/Dashboard/Folders/FolderMoveModal";
import {useIsClient} from "@/hooks/useIsClient";
import mediaFileCacheService from "@/services/MediaFileCacheService";
import {folderService} from "@/services/folderService";
import {ExtractionJobStatus} from "@/types/enums";

export type ExtractionJobsTableProps = {
    showTranslateTip?: boolean
    paginatedJobs: PaginatedSource<PaginatedExtractionJob>
    tableItems: JobTableItem[]
    initialRows: number
    onSearch: (search: string) => void
    onFeedback?: (job: PersistedExtractionJob) => void
    onBulkDownload: (jobs: PaginatedExtractionJob[]) => void,
    onRetry: (job: PersistedExtractionJob) => void
    onBulkTranslate: (jobs: PersistedExtractionJob[]) => void
    folders: Folder[]
    currentFolder: Folder | null,
    onMoveToFolder: (jobIds: number[], folderId: string | null) => void
    onFolderDelete: (folder: Folder) => void
    onFoldersRemoved?: (folderIds: string[]) => void
    onCreateFolder?: () => void
    onDragStart?: () => void
    onDragEnd?: () => void
    onFolderDrop: (e: React.DragEvent, folderId: string) => void
}

const perPageOptions = [15, 25, 50]

// Type guard functions
const isFolder = (item: JobTableItem): item is Folder => {
    return !('job_name' in item);
};

const isJob = (item: JobTableItem): item is PaginatedExtractionJob => {
    return 'job_name' in item;
};

export default function ExtractionJobsTable({
    showTranslateTip,
    paginatedJobs,
    tableItems,
    initialRows,
    onFeedback,
    onRetry,
    onBulkDownload,
    onSearch,
    onBulkTranslate,
    folders,
    currentFolder,
    onMoveToFolder,
    onFolderDelete,
    onFoldersRemoved,
    onCreateFolder,
    onDragStart,
    onDragEnd,
    onFolderDrop
}: ExtractionJobsTableProps) {
    const {
        reloadItems: reloadJobs,
        updateItems: updateJobs,
        isLoading,
        pagination
    } = paginatedJobs
    const [initialized, setInitialized] = useState(false)
    const confirm = useConfirm()
    const [selectedJobs, setSelectedJobs] = useState<Record<string, PaginatedExtractionJob>>({})
    const [selectedFolders, setSelectedFolders] = useState<Record<string, Folder>>({})
    const [lastCheckedId, setLastCheckedId] = useState<number | null>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const [showSearch, setShowSearch] = useRemember(false, 'show_search')
    const [searchValue, setSearchValue] = useRemember('', 'search_jobs')
    const searchInputRef = useRef<HTMLInputElement>(null)
    const numChecked = useMemo(() => Object.keys(selectedJobs).length + Object.keys(selectedFolders).length, [selectedJobs, selectedFolders]);
    const [draggedJob, setDraggedJob] = useState<PaginatedExtractionJob | null>(null);
    const [jobsToMove, setJobsToMove] = useState<PaginatedExtractionJob[]>([]);
    const showMoveModal = jobsToMove.length > 0;
    const [dragOverTableFolder, setDragOverTableFolder] = useState<string | null>(null);
    const isClient = useIsClient();

    useEffect(() => {
        if (showTranslateTip) {
            setTooltipOpen(true)
        }
    }, [showTranslateTip]);

    useEffect(() => {
        if (showSearch && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [showSearch]);

    useEffect(() => {
        onSearch(searchValue)
    }, [searchValue]);

    const closeToolTip = (e?: any) => {
        setTooltipOpen(false)
        e?.stopPropagation()
    }

    const toggleSearch = () => {
        setShowSearch(!showSearch)
        if (showSearch) {
            setSearchValue('')
        }
    }

    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
        } else {
            const rafId = requestAnimationFrame(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
            });
            return () => cancelAnimationFrame(rafId);
        }

    }, [pagination.currentPage]);

    const handleDelete = async (job: PersistedExtractionJob) => {
        if (!(await confirm({title: 'Delete', message: 'Are you sure you want to delete?'}))) {
            return
        }

        updateJobs(curr => curr.filter(j => j.id !== job.id));

        try {
            await deleteJob(job)
            mediaFileCacheService.clearFile(job.upload_id ?? job.job_name);
            toast.success('File deleted')
        } catch (err: any) {
            toast.error(err.message)
        } finally {
            void reloadJobs({}, {silent: true});
        }
    }

    const handleBulkDelete = async () => {
        const selectedJobsCount = Object.keys(selectedJobs).length;
        const selectedFoldersCount = Object.keys(selectedFolders).length;

        let confirmMessage = '';
        if (selectedJobsCount > 0 && selectedFoldersCount > 0) {
            confirmMessage = `Are you sure you want to delete ${selectedJobsCount} files and ${selectedFoldersCount} folders (including all files within them)? This action cannot be undone.`;
        } else if (selectedJobsCount > 0) {
            confirmMessage = `Are you sure you want to delete ${selectedJobsCount} files? This action cannot be undone.`;
        } else {
            confirmMessage = `Are you sure you want to delete ${selectedFoldersCount} folders and all files within them? This action cannot be undone.`;
        }

        if (!(await confirm({title: 'Bulk Delete', message: confirmMessage}))) {
            return
        }

        const jobsBeingDeleted = Object.values(selectedJobs);
        const folderIds = Object.keys(selectedFolders);
        const promises = [];

        if (selectedJobsCount > 0) {
            promises.push(
                axios.post(route('job.bulk-delete', {ids: Object.keys(selectedJobs)}))
            );
        }

        if (selectedFoldersCount > 0) {
            promises.push(
                folderService.deleteFolders(folderIds)
            );
        }

        updateJobs(curr => curr.filter(job => !(job.id in selectedJobs)));
        onFoldersRemoved?.(folderIds);
        setSelectedJobs({})
        setSelectedFolders({})

        try {
            await Promise.all(promises)
            jobsBeingDeleted.forEach(job => {
                mediaFileCacheService.clearFile(job.upload_id ?? job.job_name);
            });
            toast.success('Items deleted successfully')
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete some items')
        } finally {
            void reloadJobs({}, {silent: true});
        }
    }

    const bulkDownload = async () => {
        onBulkDownload(Object.values(selectedJobs))
    }

    const download = async (job: PaginatedExtractionJob) => {
        onBulkDownload([job])
    }

    const bulkTranslate = async () => {
        onBulkTranslate(
            Object.values(selectedJobs).filter(job => job.status === ExtractionJobStatus.Succeeded)
        )
    }

    const launchTranslation = (job: PersistedExtractionJob) => {
        router.visit(route('subtitle.edit', job), {
            data: {translate: true}
        })
    }

    const handleCancellation = async (job: ExtractionJob) => {
        if (!(await confirm({title: 'Cancel', message: 'Are you sure you want to cancel? Credits used for this extraction will not be refunded.'}))) {
            return
        }
        toast.promise(cancelJob(job), {
            loading: 'Cancelling...',
            success: 'Job cancelled',
            error: (err) => err.message
        })
            .catch(console.error)
            .finally(reloadJobs);
    }

    const toggleChecked = () => {
        const jobs = tableItems.filter(isJob);
        const folders = tableItems.filter(isFolder);
        const totalItems = jobs.length + folders.length;

        if (numChecked === totalItems) {
            setSelectedJobs({})
            setSelectedFolders({})
        } else {
            const newCheckedJobs: Record<string, PaginatedExtractionJob> = {}
            const newCheckedFolders: Record<string, Folder> = {}

            jobs.forEach(job => {
                newCheckedJobs[job.id] = job;
            })
            folders.forEach(folder => {
                newCheckedFolders[folder.id] = folder;
            })

            setSelectedJobs(newCheckedJobs)
            setSelectedFolders(newCheckedFolders)
        }
    }

    const toggleJobChecked = (job: PaginatedExtractionJob, event: Event) => {
        const newChecked = Object.assign({}, selectedJobs);
        const jobs = tableItems.filter(isJob);

        if ('shiftKey' in event && event.shiftKey && lastCheckedId !== null) {
            const currentIndex = jobs.findIndex(j => j.id === job.id);
            const lastIndex = Math.max(0, jobs.findIndex(j => j.id === lastCheckedId));

            const startIdx = Math.min(currentIndex, lastIndex);
            const endIdx = Math.max(currentIndex, lastIndex);

            for (let i = startIdx; i <= endIdx; i++) {
                newChecked[jobs[i].id] = jobs[i];
            }
        } else if (newChecked[job.id]) {
            delete newChecked[job.id];
        } else {
            newChecked[job.id] = job;
        }

        setLastCheckedId(job.id);
        setSelectedJobs(newChecked);
    }

    const toggleFolderChecked = (folder: Folder, event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const newChecked = Object.assign({}, selectedFolders);

        if (newChecked[folder.id]) {
            delete newChecked[folder.id];
        } else {
            newChecked[folder.id] = folder;
        }

        setSelectedFolders(newChecked);
    }

    const handleDragStart = (e: React.DragEvent, job: PaginatedExtractionJob) => {
        setDraggedJob(job);
        onDragStart?.();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', job.id.toString());

        // Create custom drag image with just filename
        const dragElement = document.createElement('div');
        dragElement.innerHTML = job.file_name;
        dragElement.style.cssText = `
            position: absolute;
            top: -1000px;
            left: -1000px;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 8px 12px;
            font-size: 14px;
            color: #374151;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            max-width: 300px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `;
        document.body.appendChild(dragElement);
        e.dataTransfer.setDragImage(dragElement, 0, 0);

        // Clean up after drag starts
        setTimeout(() => {
            document.body.removeChild(dragElement);
        }, 0);
    }

    const handleDragEnd = () => {
        setDraggedJob(null);
        onDragEnd?.();
    }

    const handleFolderClick = (folderId: string) => {
        const url = route('dashboard.folder', folderId);
        router.visit(url, { preserveState: true, preserveScroll: true });
    };

    const hasPages = paginatedJobs.pagination.total >= perPageOptions[0];
    const isLoadingRows = isLoading && !tableItems.length;
    return (
        <div className="">
            <div className="flex flex-col mt-6">
                <div className="overflow-x-auto">
                    <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden">
                            <table className="min-w-full  table-auto">
                                <thead className="">
                                <tr>
                                    <th scope="col"
                                        className="hidden md:table-cell py-4 pl-4 text-left">
                                        <Checkbox onChange={toggleChecked}/>
                                    </th>
                                    <th scope="col"
                                        className="p-3 md:p-4 text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase ">
                                        <div className="flex items-center space-x-2">
                                            {showSearch ? (
                                                <div className="relative flex items-center min-w-64">
                                                    <input
                                                        ref={searchInputRef}
                                                        type="text"
                                                        className="w-full py-1 px-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="Enter file name or job ID..."
                                                        value={searchValue}
                                                        onChange={(e) => setSearchValue(e.target.value)}
                                                        onBlur={() => {
                                                            if (!searchValue) {
                                                                setShowSearch(false)
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Escape') {
                                                                setShowSearch(false)
                                                                setSearchValue('')
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => setSearchValue('')}
                                                        className={`absolute right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ${!searchValue ? 'hidden' : ''}`}
                                                    >
                                                        <FontAwesomeIcon icon={faXmark} className="w-3 h-3"/>
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="inline-block"><button
                                                    onClick={toggleSearch}
                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                                                    title="Search files"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} className="w-3 h-3 mr-2"/>
                                                </button>
                                                    <span>Name</span>

                                                </span>
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col"
                                        className="p-2 my-3 md:p-4 text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase">
                                        Status
                                    </th>
                                    <th scope="col"
                                        className="p-2 md:p-4 hidden md:table-cell text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase">
                                        Method
                                    </th>
                                    <th scope="col"
                                        className="p-2 md:p-4 text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase ">
                                        Duration
                                    </th>
                                    <th scope="col"
                                        className="hidden lg:table-cell p-2 md:p-4 text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase ">
                                        File size
                                    </th>
                                    <th scope="col"
                                        className="p-2 md:p-4 hidden lg:table-cell text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase ">
                                        Language
                                    </th>
                                    <th scope="col"
                                        className="p-2 md:p-4 text-xs hidden xl:table-cell font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase">
                                        Created
                                    </th>
                                    <th scope="col"
                                        className="p-2 md:p-4 text-xs font-medium tracking-wider text-left text-gray-500 dark:text-gray-400 uppercase ">
                                        <button className="lg:hidden" onClick={onCreateFolder}>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                                            </svg>

                                        </button>
                                    </th>
                                </tr>
                                </thead>
                                <tbody
                                    className={`${isLoadingRows ? 'divide-y divide-gray-200 dark:divide-gray-600 animate-pulse' : ''}`}>
                                {
                                    isLoadingRows && Array.from({length: initialRows}, (_, idx) => <SkeletonTableRow
                                        key={idx}/>)
                                }
                                {
                                    tableItems.map((item, index) => {
                                        if (isFolder(item)) {
                                            return (
                                                <FolderRow
                                                    key={`folder-${item.id}`}
                                                    folder={item}
                                                    onClick={() => handleFolderClick(item.id)}
                                                    isSelected={selectedFolders[item.id] !== undefined}
                                                    onToggleSelect={(e) => toggleFolderChecked(item, e)}
                                                    onDelete={onFolderDelete}
                                                    onFolderDrop={onFolderDrop}
                                                    isDragOver={dragOverTableFolder === item.id}
                                                    onDragOver={setDragOverTableFolder}
                                                />
                                            );
                                        }

                                        const job = item;
                                        const hasUpload = item.upload_expires_at !== null &&
                                            moment().isBefore(item.upload_expires_at);

                                        return (
                                        <tr key={job.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, job)}
                                            onDragEnd={handleDragEnd}
                                            className={`border hover:bg-gray-50 dark:hover:bg-gray-800 border-bottom-gray-200 dark:border-gray-700 border-l-0 border-r-0  ${!hasPages ? 'border-b-0' : ''} ${draggedJob?.id === job.id ? 'opacity-50' : ''}`}>
                                            <td className="py-4 pl-4 hidden md:table-cell">
                                                <Checkbox onChange={(e) => toggleJobChecked(job, e.nativeEvent)}
                                                          checked={selectedJobs[job.id] !== undefined}/>
                                            </td>
                                            <td className="min-w-0 py-1 md:py-0 max-w-[8rem] sm:max-w-xs md:max-w-sm text-sm text-gray-900 dark:text-gray-100 ">
                                                {
                                                    job.status === ExtractionJobStatus.Succeeded && (job.subtitle_count !== 0) ? (
                                                            <Link
                                                                className="2xl:truncate block cursor-pointer px-3 p-2 md:p-4 hover:underline"
                                                                href={route('subtitle.edit', job)}>
                                                                <span
                                                                    className="line-clamp-2 break-words 2xl:line-clamp-none 2xl:inline 2xl:truncate">{job.file_name}</span>
                                                            </Link>) :
                                                        <div className="2xl:truncate block px-3 p-2 md:p-4">
                                                            <span
                                                                className="line-clamp-2 break-words 2xl:line-clamp-none 2xl:inline 2xl:truncate">{job.file_name}</span>
                                                        </div>


                                                }

                                            </td>
                                            <td className="p-2 pl-4 md:p-4 text md:pl-5">
                                                <JobStatusIndicator job={job}/>
                                            </td>
                                            <td className="p-2 md:p-4 hidden md:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 ">
                                                {ucFirst(job.method === 'ocr' ? 'vision' : job.method)}
                                            </td>
                                            <td className="p-2 md:p-4 text-sm font-normal text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                                {job.file_duration ? secondsToDuration(job.file_duration) : 'N/A'}
                                            </td>
                                            <td className="p-2 md:p-4 hidden lg:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {job.file_size ? bytesToSize(job.file_size) : 'N/A'}
                                            </td>
                                            <td className="p-2 md:p-4 hidden lg:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap  items-center">
                                                {getLanguageName(job.language)}
                                            </td>
                                            <td className="p-2 md:p-4 hidden xl:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap ">
                                                {isClient ?
                                                    <span>{moment(job.created_at).format('MMM D, h:mm A')}</span> :
                                                    <span className="invisible">MMM DD, HH:MM AM</span>}
                                            </td>
                                            <td className="text-start pr-2 md:px-4">

                                                {
                                                    !isJobRunning(job.status) && (
                                                        <Dropdown onOpen={() => setTooltipOpen(false)}>
                                                            <Dropdown.Trigger>
                                                                {
                                                                    (isOpen) => (
                                                                        <Popover open={index === 0 && tooltipOpen}
                                                                                 placement='top'
                                                                                 content={<TranslationTooltip
                                                                                     onClose={closeToolTip}/>}>

                                                                            <OptionsButton isOpen={isOpen} />
                                                                        </Popover>
                                                                    )
                                                                }
                                                            </Dropdown.Trigger>
                                                            <Dropdown.Content className="sm:mt-1">

                                                                {
                                                                    (job.status === ExtractionJobStatus.Succeeded && job.subtitle_count !== 0) && (
                                                                        <span>
                                                                            <Dropdown.Item
                                                                                title="Download"
                                                                                onClick={() => download(job)}>
                                                                                <FontAwesomeIcon
                                                                                    className="text-gray-500  w-4 h-4 pr-2 "
                                                                                    icon={faCircleDown}/>
                                                                                <span>Download</span>
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Link title="Edit"
                                                                                           href={route('subtitle.edit', job)}>
                                                                                <FontAwesomeIcon
                                                                                    className="text-gray-500 w-4 h-4 pr-2 "
                                                                                    icon={faPen}/>
                                                                                <span>Edit</span>
                                                                            </Dropdown.Link>
                                                                            <Dropdown.Item
                                                                                onClick={() => launchTranslation(job)}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                                                     fill="none" viewBox="0 0 24 24"
                                                                                     strokeWidth="1.5"
                                                                                     stroke="currentColor"
                                                                                     className="text-gray-500  w-7 h-7 pr-2">
                                                                                    <path strokeLinecap="round"
                                                                                          strokeLinejoin="round"
                                                                                          d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802"/>
                                                                                </svg><span>Translate</span>
                                                                            </Dropdown.Item>
                                                                        </span>
                                                                    )
                                                                }
                                                                {
                                                                    folders.length > 0 && (
                                                                        <Dropdown.Item onClick={() => {
                                                                            setJobsToMove([job]);
                                                                        }}>
                                                                            <FontAwesomeIcon
                                                                                className="text-gray-500 w-4 h-4 pr-2"
                                                                                icon={faArrowRight}/>
                                                                            <span>Move</span>
                                                                        </Dropdown.Item>
                                                                    )
                                                                }
                                                                {

                                                                    (job.status === ExtractionJobStatus.Failed && job.method !== 'manual' && hasUpload) &&
                                                                    (
                                                                        <Dropdown.Item title="Retry"
                                                                                       onClick={() => onRetry(job)}>
                                                                            <FontAwesomeIcon
                                                                                className="text-gray-500 w-4 h-4 pr-2 flex self-center"
                                                                                icon={faRotateRight}/>
                                                                            <span>Retry</span>
                                                                        </Dropdown.Item>
                                                                    )
                                                                }
                                                                {isJobRunning(job.status) && (
                                                                    <Dropdown.Item
                                                                        onClick={() => handleCancellation(job)}>
                                                                        <FontAwesomeIcon
                                                                            className="text-gray-500 w-4 h-4 pr-2 "
                                                                            icon={faCircleXmark}/>
                                                                        <span>Cancel</span>
                                                                    </Dropdown.Item>
                                                                )}
                                                                {
                                                                    !isJobRunning(job.status) &&
                                                                    job.status !== ExtractionJobStatus.Cancelled &&
                                                                    job.status !== ExtractionJobStatus.Failed &&
                                                                    job.method !== 'manual' &&
                                                                    (
                                                                        <Dropdown.Item title="Give Feedback"
                                                                                       onClick={() => onFeedback?.(job)}>
                                                                            <FontAwesomeIcon
                                                                                className="text-gray-500 w-4 h-4 pr-2 flex self-center"
                                                                                icon={faMessage}/>
                                                                            <span>Give Feedback</span>
                                                                        </Dropdown.Item>
                                                                    )
                                                                }
                                                                {
                                                                    (
                                                                        [ExtractionJobStatus.Succeeded, ExtractionJobStatus.Cancelled].includes(job.status) &&
                                                                        job.method !== 'manual' &&
                                                                        hasUpload
                                                                    ) &&
                                                                    (
                                                                        <Dropdown.Item title="Re-extract"
                                                                                       onClick={() => onRetry(job)}>
                                                                            <FontAwesomeIcon
                                                                                className="text-gray-500 w-4 h-4 pr-2 flex self-center"
                                                                                icon={faRotateRight}/>
                                                                            <span>Re-extract</span>
                                                                        </Dropdown.Item>
                                                                    )
                                                                }
                                                                {
                                                                    !isJobRunning(job.status) && (
                                                                        <Dropdown.Item onClick={() => handleDelete(job)}>
                                                                            <FontAwesomeIcon
                                                                                title="Delete"
                                                                                className="text-gray-500 w-4 h-4  pr-2 "
                                                                                icon={faTrashCan}/>
                                                                            <span>Delete</span>
                                                                        </Dropdown.Item>
                                                                    )
                                                                }
                                                            </Dropdown.Content>
                                                        </Dropdown>
                                                    )
                                                }

                                                {
                                                    isJobRunning(job.status) && (
                                                        <Tooltip
                                                            content={<span>Cancel</span>}
                                                            placement="auto"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCancellation(job)}
                                                                className=" flex text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition ease-in-out duration-150"
                                                            >
                                                                <FontAwesomeIcon
                                                                    className="text-gray-500 w-4 h-4 px-2 py-1 hover:text-red-600"
                                                                    icon={faCircleXmark}/>
                                                            </button>
                                                        </Tooltip>

                                                    )
                                                }


                                            </td>
                                        </tr>
                                        );
                                    })
                                }


                                </tbody>
                                {
                                    numChecked > 0 && (
                                        <tfoot>
                                        <tr>
                                            <td colSpan={9}
                                                className={`p-2 px-6 ${hasPages ? 'border-solid border-0 border-b border-gray-200 dark:border-gray-700' : ''} bg-gray-50 dark:bg-gray-800 rounded-lg`}>
                                                <div className="flex grow items-center">
                                                    <p className="text-sm mr-4">
                                                    <span
                                                        className="font-normal text-gray-500 dark:text-gray-400"><span>{numChecked}</span> Selected</span>

                                                    </p>
                                                    <Dropdown>
                                                        <Dropdown.Trigger>
                                                            <SecondaryButton ><span
                                                                className="hidden md:inline">Bulk Actions</span>
                                                                <svg className="ms-2 inline h-5 w-5"
                                                                     xmlns="http://www.w3.org/2000/svg"
                                                                     viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd"
                                                                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                          clipRule="evenodd"></path>
                                                                </svg>
                                                            </SecondaryButton>
                                                        </Dropdown.Trigger>
                                                        <Dropdown.Content className="sm:mt-1 text-right" align={"left"}>
                                                            <Dropdown.Item onClick={handleBulkDelete}>
                                                                <FontAwesomeIcon
                                                                    icon={faTrashCan}
                                                                    className={`me-2`}
                                                                /> <span>Delete</span></Dropdown.Item>
                                                            {Object.keys(selectedFolders).length === 0 && (
                                                                <>
                                                                    <Dropdown.Item onClick={bulkDownload}>
                                                                        <FontAwesomeIcon
                                                                            icon={faDownload}
                                                                            className={`me-2`}
                                                                        /> <span>Download</span>
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item onClick={bulkTranslate}>
                                                                        <span className={`flex items-center`}>
                                                                            <TranslateIcon/>
                                                                             <span>Translate</span>
                                                                        </span>
                                                                    </Dropdown.Item>
                                                                    {
                                                                        folders.length > 0 && (
                                                                            <Dropdown.Item onClick={() => {
                                                                                setJobsToMove(Object.values(selectedJobs));
                                                                            }}>
                                                                                <FontAwesomeIcon
                                                                                    icon={faArrowRight}
                                                                                    className="me-2 w-4 h-4"/>
                                                                                <span>Move</span>
                                                                            </Dropdown.Item>
                                                                        )
                                                                    }
                                                                </>
                                                            )}
                                                            <Dropdown.Item onClick={() => {
                                                                setSelectedJobs({});
                                                                setSelectedFolders({});
                                                            }}>
                                                                <FontAwesomeIcon
                                                                    icon={faXmark}
                                                                    className={` md:me-2`}
                                                                /> <span>Deselect All</span>
                                                            </Dropdown.Item>
                                                        </Dropdown.Content>
                                                    </Dropdown>

                                                </div>
                                            </td>
                                        </tr>
                                        </tfoot>
                                    )
                                }
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {
                hasPages && (
                    <div className="flex justify-between items-center mt-4 mx-6 mb-5">
                        <PaginationControl perPageOptions={perPageOptions} source={paginatedJobs}/>
                    </div>
                )
            }

            <FolderMoveModal
                show={showMoveModal}
                folders={folders}
                currentFolderId={currentFolder?.id}
                onClose={() => {
                    setJobsToMove([]);
                }}
                onMove={(folderId) => {
                    if (jobsToMove.length > 0) {
                        onMoveToFolder(jobsToMove.map(job => job.id), folderId);
                        if (jobsToMove.length > 1) {
                            setSelectedJobs({});
                        }
                    }
                }}
            />
        </div>
    )
}

function FolderRow({
    folder,
    onClick,
    isSelected,
    onToggleSelect,
    onDelete,
    onFolderDrop,
    isDragOver,
    onDragOver
}: {
    folder: Folder;
    onClick: () => void;
    isSelected: boolean;
    onToggleSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDelete: (folder: Folder) => void;
    onFolderDrop: (e: React.DragEvent, folderId: string) => void;
    isDragOver: boolean;
    onDragOver: (folderId: string | null) => void;
}) {
    const isClient = useIsClient();

    return (
        <tr
            className={`border hover:bg-gray-50 dark:hover:bg-gray-800 border-bottom-gray-200 dark:border-gray-700 border-l-0 border-r-0 ${isDragOver ? 'bg-blue-50 dark:bg-blue-900 border-blue-300 dark:border-blue-600' : ''}`}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDragOver(folder.id);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDragOver(null);
            }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFolderDrop(e, folder.id);
                onDragOver(null);
            }}
        >
            <td className="py-4 pl-4 hidden md:table-cell">
                <Checkbox
                    onChange={(e) => onToggleSelect(e)}
                    checked={isSelected}
                />
            </td>
            <td className="max-w-25 py-1 md:py-0 md:max-w-sm text-sm text-gray-900 dark:text-gray-100">
                <div className="flex items-center px-3 p-2 md:p-4">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                        }}
                        className="flex items-center font-medium hover:underline"
                    >
                        <FontAwesomeIcon
                            icon={faFolder}
                            className="mr-3 text-blue-500 w-4 h-4"
                        />
                        {folder.name}
                    </button>
                    <span
                        className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-medium ml-2 px-1.5 py-0.5 rounded ">{folder.extraction_jobs_count || 0} {(folder.extraction_jobs_count || 0) === 1 ? 'file' : 'files'}</span>
                </div>
            </td>
            <td className="p-2 pl-4 md:p-4 text md:pl-5">
                {/* Empty status cell for folders */}
            </td>
            <td className="p-2 md:p-4 hidden md:table-cell text-sm font-normal text-gray-500 dark:text-gray-400">
                {/* Empty method cell */}
            </td>
            <td className="p-2 md:p-4 text-sm font-normal text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {/* Empty duration cell */}
            </td>
            <td className="p-2 md:p-4 hidden lg:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {/* Empty size cell */}
            </td>
            <td className="p-2 md:p-4 hidden lg:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {/* Empty language cell */}
            </td>
            <td className="p-2 md:p-4 hidden xl:table-cell text-sm font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {isClient ?
                    <span>{moment(folder.created_at).format('MMM D, h:mm A')}</span> :
                    <span className="invisible">MMM DD, HH:MM AM</span>
                }
            </td>
            <td className="text-start pr-2 md:px-4">
                <Dropdown>
                    <Dropdown.Trigger>
                        {(isOpen) => (
                            <OptionsButton isOpen={isOpen} />
                        )}
                    </Dropdown.Trigger>
                    <Dropdown.Content className="sm:mt-1">
                        <Dropdown.Item onClick={() => onDelete(folder)}>
                            <FontAwesomeIcon className="text-gray-500 w-4 h-4 pr-2" icon={faTrashCan}/>
                            <span>Delete</span>
                        </Dropdown.Item>
                    </Dropdown.Content>
                </Dropdown>
            </td>
        </tr>
    );
}

function SkeletonTableRow() {
    const widths = [
        'w-[50%] lg:w-[50%]',
        'w-[60%] lg:w-[55%]',
        'w-[70%] lg:w-[65%]',
        'w-[80%] lg:w-[70%]',
    ]
    const [width, setWidth] = useState('')

    useEffect(() => {
        const randomWidth = widths[Math.floor(Math.random() * widths.length)]
        setWidth(randomWidth)
    }, [])
    return (
        <tr>
            <td colSpan={9} className="p-4">
                <div className="flex w-full items-center-center py-1">
                    <div
                        className={`h-2 bg-gray-200 dark:bg-gray-700 rounded-full w-4 mr-4 hidden md:block`}></div>
                    <div className={`h-2 bg-gray-200 dark:bg-gray-700 rounded-full mr-4 ${width}`}></div>
                </div>
            </td>
        </tr>
    )
}

function TranslationTooltip({onClose}: { onClose?: () => void }) {
    return (
        <div className="p-5 w-64">
            <div className="flex">
                <svg xmlns="http://www.w3.org/2000/svg"
                     fill="none" viewBox="0 0 24 24"
                     strokeWidth="1.5"
                     stroke="currentColor"
                     className="text-gray-500 dark:text-gray-400 w-7 h-7 pr-2">
                    <path strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802"/>
                </svg>
                <h3 className="font-semibold mb-4 grow dark:text-gray-200">Translation</h3>
                <FontAwesomeIcon
                    onClick={onClose}
                    title={"Close"}
                    className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-2 cursor-pointer "
                    icon={faXmark}/>
            </div>

            <p className="text-sm dark:text-gray-300">
                You can translate the subtitles to another language by clicking the menu button and selecting
                "Translate".
            </p>
        </div>
    )
}

function OptionsButton({ isOpen }: { isOpen: boolean}) {
    return (
        <button
            type="button"
            className={`menu-button px-1 py-1 flex text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition ease-in-out duration-150 ${isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-900'}`}
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
            </svg>

        </button>
    )
}
